import {Schema, NodeSpec, MarkSpec, DOMOutputSpec} from "prosemirror-model"
import { addSuggestionMarks } from "prosemirror-suggestion-mode"
// Add list support
import { orderedList, bulletList, listItem } from "prosemirror-schema-list"
// Table support - use prosemirror-tables for compatibility
import { tableNodes } from "prosemirror-tables"

const pDOM: DOMOutputSpec = ["p", 0], blockquoteDOM: DOMOutputSpec = ["blockquote", 0],
      hrDOM: DOMOutputSpec = ["hr"], preDOM: DOMOutputSpec = ["pre", ["code", 0]],
      brDOM: DOMOutputSpec = ["br"]

// Common attribute patterns
const commonAttrs = {
  revision_id: {default: null},
  change_status: {default: null},
  id: {default: null},
  // Paragraph/heading style linkage (resolved via StyleRegistry)
  style_id: { default: 'normal' },
  // Text alignment support
  textAlign: { default: null }, // null, 'left', 'center', 'right', 'justify'
  // Inline style overrides (selection-scoped)
  fontSize: { default: null, validate: "number|null" }, // pt
  lineHeight: { default: null, validate: "number|null" }, // unitless
  spaceBefore: { default: null, validate: "number|null" }, // pt
  spaceAfter: { default: null, validate: "number|null" }, // pt
}

const getCommonAttrs = (dom: HTMLElement) => {
  // Helper to parse style value
  const getStyleValue = (styleName: string): string | null => {
    if (!dom.style) return null;
    return (dom.style as any)[styleName] || null;
  };
  
  // Helper to convert CSS units to points
  const cssToPoints = (value: string | null): number | null => {
    if (!value) return null;
    const num = parseFloat(value);
    if (isNaN(num)) return null;
    
    // Convert common CSS units to points (1pt = 1pt, 1px ≈ 0.75pt, 1in = 72pt)
    if (value.endsWith('pt')) return num;
    if (value.endsWith('px')) return num * 0.75;
    if (value.endsWith('in')) return num * 72;
    if (value.endsWith('cm')) return num * 28.35;
    if (value.endsWith('mm')) return num * 2.835;
    return num; // Assume pt if no unit
  };
  
  return {
    change_status: dom.getAttribute("data-change-status"),
    revision_id: dom.getAttribute("data-revision-id"),
    id: dom.getAttribute("id"),
    style_id: dom.getAttribute("data-style-id"),
    // Read textAlign from data attribute OR inline style
    textAlign: dom.getAttribute("data-text-align") || getStyleValue('textAlign'),
    fontSize: dom.getAttribute("data-font-size") ? parseFloat(dom.getAttribute("data-font-size")!) : null,
    // Read lineHeight from data attribute OR inline style (strip 'px' if present)
    lineHeight: (() => {
      const dataValue = dom.getAttribute("data-line-height");
      if (dataValue) return parseFloat(dataValue);
      const styleValue = getStyleValue('lineHeight');
      if (styleValue) {
        // Convert line-height from various formats
        // "1.5" → 1.5 (unitless)
        // "22px" → parse as pixels (we'll store as unitless for now)
        // "150%" → 1.5
        if (styleValue.endsWith('px')) {
          // Try to convert px to unitless by dividing by font-size
          return parseFloat(styleValue);
        } else if (styleValue.endsWith('%')) {
          return parseFloat(styleValue) / 100;
        } else {
          return parseFloat(styleValue);
        }
      }
      return null;
    })(),
    // Read spaceBefore/After from data attributes OR margin-top/bottom styles
    spaceBefore: (() => {
      const dataValue = dom.getAttribute("data-space-before");
      if (dataValue) return parseFloat(dataValue);
      return cssToPoints(getStyleValue('marginTop'));
    })(),
    spaceAfter: (() => {
      const dataValue = dom.getAttribute("data-space-after");
      if (dataValue) return parseFloat(dataValue);
      return cssToPoints(getStyleValue('marginBottom'));
    })(),
  }
}

const toCommonDOM = (attrs: any) => ({
  "data-change-status": attrs.change_status,
  "data-revision-id": attrs.revision_id,
  id: attrs.id,
  ...(attrs.style_id ? { "data-style-id": attrs.style_id } : {}),
  ...(attrs.textAlign ? { "data-text-align": attrs.textAlign } : {}),
  ...(attrs.fontSize ? { "data-font-size": attrs.fontSize } : {}),
  ...(attrs.lineHeight ? { "data-line-height": attrs.lineHeight } : {}),
  ...(attrs.spaceBefore ? { "data-space-before": attrs.spaceBefore } : {}),
  ...(attrs.spaceAfter ? { "data-space-after": attrs.spaceAfter } : {})
})

// Create prosemirror-tables compatible table nodes (simplified for compatibility)
const compatibleTableNodes = tableNodes({
  tableGroup: 'block',
  cellContent: 'paragraph+',
  cellAttributes: {
    // Simplified attributes that work reliably with prosemirror-tables
    background: {
      default: null,
      getFromDOM: (dom: HTMLElement) => dom.style.backgroundColor || null,
      setDOMAttr: (value: unknown, attrs: any) => {
        if (value && typeof value === 'string') {
          attrs.style = (attrs.style || '') + `background-color: ${value};`;
        }
      }
    }
  }
});


/// [Specs](#model.NodeSpec) for the nodes defined in this schema.
export const nodes = {
  /// NodeSpec The top level document node.
  doc: {
    content: "block+"  // ← CHANGED: Direct blocks, no page containers!
  } as NodeSpec,

  /// A plain paragraph textblock. Represented in the DOM
  /// as a `<p>` element.
  paragraph: {
    content: "inline*",
    group: "block",
    attrs: commonAttrs,
    parseDOM: [{tag: "p", getAttrs: (dom: HTMLElement) => {
      const classStyleId = (dom.getAttribute('class')||'').split(/\s+/).find(c => c.startsWith('pm-style-'))?.slice('pm-style-'.length);
      const dataStyleId = dom.getAttribute('data-style-id');
      const commonAttrs = getCommonAttrs(dom);
      
      // Also check deprecated HTML align attribute (Word uses this)
      const alignAttr = dom.getAttribute('align');
      if (alignAttr && !commonAttrs.textAlign) {
        commonAttrs.textAlign = alignAttr.toLowerCase();
      }
      
      return { ...commonAttrs, style_id: classStyleId || dataStyleId || 'normal' };
    }}],
    toDOM(node) { 
      const cls = node.attrs.style_id ? `pm-style-${node.attrs.style_id}` : undefined;
      const alignClass = node.attrs.textAlign ? `pm-text-align-${node.attrs.textAlign}` : undefined;
      const base = toCommonDOM(node.attrs) as any;
      if (cls || alignClass) {
        const classes = [cls, alignClass].filter(Boolean).join(' ');
        base.class = (base.class ? base.class + ' ' : '') + classes;
      }
      // Apply inline style overrides
      const inlineStyles = [];
      if (node.attrs.fontSize) inlineStyles.push(`font-size: ${node.attrs.fontSize}pt`);
      if (node.attrs.lineHeight) inlineStyles.push(`line-height: ${node.attrs.lineHeight}`);
      if (node.attrs.spaceBefore) inlineStyles.push(`margin-top: ${node.attrs.spaceBefore}pt`);
      if (node.attrs.spaceAfter) inlineStyles.push(`margin-bottom: ${node.attrs.spaceAfter}pt`);
      if (inlineStyles.length > 0) {
        base.style = (base.style || '') + inlineStyles.join('; ');
      }
      
      return ["p", base, 0];
    }
  } as NodeSpec,

  /// A blockquote (`<blockquote>`) wrapping one or more blocks.
  blockquote: {
    content: "block+",
    group: "block",
    defining: true,
    attrs: commonAttrs,
    parseDOM: [{tag: "blockquote", getAttrs: getCommonAttrs}],
    toDOM(node) { 
      const base = toCommonDOM(node.attrs) as any;
      // Apply inline style overrides
      const inlineStyles = [];
      if (node.attrs.fontSize) inlineStyles.push(`font-size: ${node.attrs.fontSize}pt`);
      if (node.attrs.lineHeight) inlineStyles.push(`line-height: ${node.attrs.lineHeight}`);
      if (node.attrs.spaceBefore) inlineStyles.push(`margin-top: ${node.attrs.spaceBefore}pt`);
      if (node.attrs.spaceAfter) inlineStyles.push(`margin-bottom: ${node.attrs.spaceAfter}pt`);
      if (inlineStyles.length > 0) {
        base.style = (base.style || '') + inlineStyles.join('; ');
      }
      return ["blockquote", base, 0];
    }
  } as NodeSpec,

  /// A horizontal rule (`<hr>`).
  horizontal_rule: {
    group: "block",
    attrs: commonAttrs,
    parseDOM: [{tag: "hr", getAttrs: getCommonAttrs}],
    toDOM(node) { 
      return ["hr", toCommonDOM(node.attrs)];
    }
  } as NodeSpec,

  /// A heading textblock, with a `level` attribute that
  /// should hold the number 1 to 6. Parsed and serialized as `<h1>` to
  /// `<h6>` elements.
  heading: {
    content: "inline*",
    group: "block",
    defining: true,
    attrs: {
      level: {default: 1, validate: "number"},
      // Numbering attributes for legal outline rendering
      num_style_id: { default: null },
      num_restart: { default: false },
      num_start: { default: 1, validate: "number" },
      ...commonAttrs
    },
    parseDOM: [
      {tag: "h1", getAttrs: (dom: HTMLElement) => {
        const commonAttrs = getCommonAttrs(dom);
        const alignAttr = dom.getAttribute('align');
        if (alignAttr && !commonAttrs.textAlign) commonAttrs.textAlign = alignAttr.toLowerCase();
        return {level: 1, num_style_id: dom.getAttribute('data-num-style-id'), num_restart: dom.getAttribute('data-num-restart') === 'true', num_start: dom.getAttribute('data-num-start') ? parseInt(dom.getAttribute('data-num-start')!) : 1, ...commonAttrs};
      }},
      {tag: "h2", getAttrs: (dom: HTMLElement) => {
        const commonAttrs = getCommonAttrs(dom);
        const alignAttr = dom.getAttribute('align');
        if (alignAttr && !commonAttrs.textAlign) commonAttrs.textAlign = alignAttr.toLowerCase();
        return {level: 2, num_style_id: dom.getAttribute('data-num-style-id'), num_restart: dom.getAttribute('data-num-restart') === 'true', num_start: dom.getAttribute('data-num-start') ? parseInt(dom.getAttribute('data-num-start')!) : 1, ...commonAttrs};
      }},
      {tag: "h3", getAttrs: (dom: HTMLElement) => {
        const commonAttrs = getCommonAttrs(dom);
        const alignAttr = dom.getAttribute('align');
        if (alignAttr && !commonAttrs.textAlign) commonAttrs.textAlign = alignAttr.toLowerCase();
        return {level: 3, num_style_id: dom.getAttribute('data-num-style-id'), num_restart: dom.getAttribute('data-num-restart') === 'true', num_start: dom.getAttribute('data-num-start') ? parseInt(dom.getAttribute('data-num-start')!) : 1, ...commonAttrs};
      }},
      {tag: "h4", getAttrs: (dom: HTMLElement) => {
        const commonAttrs = getCommonAttrs(dom);
        const alignAttr = dom.getAttribute('align');
        if (alignAttr && !commonAttrs.textAlign) commonAttrs.textAlign = alignAttr.toLowerCase();
        return {level: 4, num_style_id: dom.getAttribute('data-num-style-id'), num_restart: dom.getAttribute('data-num-restart') === 'true', num_start: dom.getAttribute('data-num-start') ? parseInt(dom.getAttribute('data-num-start')!) : 1, ...commonAttrs};
      }},
      {tag: "h5", getAttrs: (dom: HTMLElement) => {
        const commonAttrs = getCommonAttrs(dom);
        const alignAttr = dom.getAttribute('align');
        if (alignAttr && !commonAttrs.textAlign) commonAttrs.textAlign = alignAttr.toLowerCase();
        return {level: 5, num_style_id: dom.getAttribute('data-num-style-id'), num_restart: dom.getAttribute('data-num-restart') === 'true', num_start: dom.getAttribute('data-num-start') ? parseInt(dom.getAttribute('data-num-start')!) : 1, ...commonAttrs};
      }},
      {tag: "h6", getAttrs: (dom: HTMLElement) => {
        const commonAttrs = getCommonAttrs(dom);
        const alignAttr = dom.getAttribute('align');
        if (alignAttr && !commonAttrs.textAlign) commonAttrs.textAlign = alignAttr.toLowerCase();
        return {level: 6, num_style_id: dom.getAttribute('data-num-style-id'), num_restart: dom.getAttribute('data-num-restart') === 'true', num_start: dom.getAttribute('data-num-start') ? parseInt(dom.getAttribute('data-num-start')!) : 1, ...commonAttrs};
      }}
    ],
    toDOM(node) { 
      const base = toCommonDOM(node.attrs) as any;
      const styleClass = node.attrs.style_id ? `pm-style-${node.attrs.style_id}` : undefined;
      const alignClass = node.attrs.textAlign ? `pm-text-align-${node.attrs.textAlign}` : undefined;
      if (styleClass || alignClass) {
        const classes = [styleClass, alignClass].filter(Boolean).join(' ');
        base.class = (base.class ? base.class + ' ' : '') + classes;
      }
      if (node.attrs.num_style_id) base['data-num-style-id'] = node.attrs.num_style_id;
      if (node.attrs.num_restart) base['data-num-restart'] = String(!!node.attrs.num_restart);
      if (node.attrs.num_start && node.attrs.num_start !== 1) base['data-num-start'] = String(node.attrs.num_start);
      // Apply inline style overrides
      const inlineStyles = [];
      if (node.attrs.fontSize) inlineStyles.push(`font-size: ${node.attrs.fontSize}pt`);
      if (node.attrs.lineHeight) inlineStyles.push(`line-height: ${node.attrs.lineHeight}`);
      if (node.attrs.spaceBefore) inlineStyles.push(`margin-top: ${node.attrs.spaceBefore}pt`);
      if (node.attrs.spaceAfter) inlineStyles.push(`margin-bottom: ${node.attrs.spaceAfter}pt`);
      if (inlineStyles.length > 0) {
        base.style = (base.style || '') + inlineStyles.join('; ');
      }
      return ["h" + node.attrs.level, base, 0];
    }
  } as NodeSpec,

  /// A code listing. Disallows marks or non-text inline
  /// nodes by default. Represented as a `<pre>` element with a
  /// `<code>` element inside of it.
  code_block: {
    content: "text*",
    marks: "",
    group: "block",
    code: true,
    defining: true,
    attrs: {
      ...commonAttrs,
      language: { default: null }
    },
    parseDOM: [{
      tag: "pre", 
      preserveWhitespace: "full", 
      getAttrs: (dom: HTMLElement) => {
        const dataLang = dom.getAttribute('data-language');
        if (dataLang) {
          return { ...getCommonAttrs(dom), language: dataLang };
        }
        
        const codeEl = dom.querySelector('code');
        if (codeEl) {
          const className = codeEl.getAttribute('class') || '';
          const classes = className.split(/\s+/);
          for (const cls of classes) {
            const lang = cls.replace(/^language-/, '');
            if (lang && lang !== cls && /^[a-zA-Z0-9_+-]+$/.test(lang)) {
              return { ...getCommonAttrs(dom), language: lang };
            }
          }
        }
        
        return { ...getCommonAttrs(dom), language: null };
      }
    }],
    toDOM(node) { 
      const attrs = toCommonDOM(node.attrs) as any;
      if (node.attrs.language) {
        attrs['data-language'] = node.attrs.language;
        attrs.class = (attrs.class || '') + ` language-${node.attrs.language}`;
      }
      return ["pre", attrs, ["code", { class: node.attrs.language ? `language-${node.attrs.language}` : undefined }, 0]];
    }
  } as NodeSpec,

  // Add list support with custom attributes for change tracking
  ordered_list: {
    ...orderedList,
    content: "list_item+",
    group: "block",
    attrs: {
      ...orderedList.attrs,
      // Numbering style binding and restart flag
      num_style_id: { default: null },
      num_restart: { default: false },
      num_start: { default: 1 },
      ...commonAttrs
    },
    parseDOM: [{
      tag: "ol", 
      getAttrs: (dom: HTMLElement) => ({
        order: dom.hasAttribute("start") ? +dom.getAttribute("start")! : 1,
        num_style_id: dom.getAttribute('data-num-style-id'),
        num_restart: dom.getAttribute('data-num-restart') === 'true',
        num_start: dom.getAttribute('data-num-start') ? parseInt(dom.getAttribute('data-num-start')!) : 1,
        ...getCommonAttrs(dom)
      })
    }],
    toDOM(node) { 
      const base: any = toCommonDOM(node.attrs);
      if (node.attrs.num_style_id) base['data-num-style-id'] = node.attrs.num_style_id;
      if (node.attrs.num_restart) base['data-num-restart'] = String(!!node.attrs.num_restart);
      if (node.attrs.num_start && node.attrs.num_start !== 1) base['data-num-start'] = String(node.attrs.num_start);
      return node.attrs.order == 1 ? 
        ["ol", base, 0] : 
        ["ol", {start: node.attrs.order, ...base}, 0];
    }
  } as NodeSpec,

  bullet_list: {
    ...bulletList,
    content: "list_item+",
    group: "block",
    attrs: {
      ...commonAttrs,
      bulletStyle: { default: 'disc' } // 'disc' | 'dash' | 'chevron' | 'pointer' | 'circle' | 'square' | 'checkbox'
    },
    parseDOM: [{
      tag: "ul", 
      getAttrs: (dom: HTMLElement) => ({
        ...getCommonAttrs(dom),
        bulletStyle: dom.getAttribute('data-bullet-style') || 'disc'
      })
    }],
    toDOM(node) { 
      const attrs = toCommonDOM(node.attrs) as any;
      attrs['data-bullet-style'] = node.attrs.bulletStyle;
      if (node.attrs.bulletStyle === 'checkbox') {
        attrs.class = (attrs.class || '') + ' checkbox-list';
      }
      return ["ul", attrs, 0];
    }
  } as NodeSpec,

  list_item: {
    ...listItem,
    content: "paragraph block*",
    attrs: {
      ...commonAttrs,
      bulletStyle: { default: null }, // optional per-item override
      checked: { default: null }, // null (not a checkbox), true (checked), false (unchecked)
      unmarked: { default: false } // true when bullet/number mark has been removed via backspace
    },
    parseDOM: [{
      tag: "li", 
      getAttrs: (dom: HTMLElement) => ({
        ...getCommonAttrs(dom),
        bulletStyle: dom.getAttribute('data-bullet-style') || null,
        checked: dom.hasAttribute('data-checked') ? dom.getAttribute('data-checked') === 'true' : null,
        unmarked: dom.getAttribute('data-unmarked') === 'true'
      })
    }],
    toDOM(node) { 
      const base = toCommonDOM(node.attrs) as any;
      const styleClass = node.attrs.style_id ? `pm-style-${node.attrs.style_id}` : undefined;
      const alignClass = node.attrs.textAlign ? `pm-text-align-${node.attrs.textAlign}` : undefined;
      if (styleClass || alignClass) {
        const classes = [styleClass, alignClass].filter(Boolean).join(' ');
        base.class = (base.class ? base.class + ' ' : '') + classes;
      }
      if (node.attrs.bulletStyle) {
        base['data-bullet-style'] = node.attrs.bulletStyle;
      }
      if (node.attrs.checked !== null) {
        base['data-checked'] = String(node.attrs.checked);
        base.class = (base.class || '') + ' checkbox-item';
      }
      if (node.attrs.unmarked) {
        base['data-unmarked'] = 'true';
        base.class = (base.class || '') + ' unmarked-item';
      }
      // Apply inline style overrides
      const inlineStyles = [];
      if (node.attrs.fontSize) inlineStyles.push(`font-size: ${node.attrs.fontSize}pt`);
      if (node.attrs.lineHeight) inlineStyles.push(`line-height: ${node.attrs.lineHeight}`);
      if (node.attrs.spaceBefore) inlineStyles.push(`margin-top: ${node.attrs.spaceBefore}pt`);
      if (node.attrs.spaceAfter) inlineStyles.push(`margin-bottom: ${node.attrs.spaceAfter}pt`);
      if (inlineStyles.length > 0) {
        base.style = (base.style || '') + inlineStyles.join('; ');
      }
      return ["li", base, 0];
    }
  } as NodeSpec,

  // Use prosemirror-tables nodes directly (maximum compatibility)
  ...compatibleTableNodes,

  /// The text node.
  text: {
    group: "inline"
  } as NodeSpec,

  /// A block-level image with alignment and sizing options
  image: {
    inline: false,
    attrs: {
      src: {validate: "string"},
      alt: {default: null, validate: "string|null"},
      title: {default: null, validate: "string|null"},
      width: {default: null, validate: "number|null"},
      height: {default: null, validate: "number|null"},
      alignment: {default: "left", validate: "string"}, // left, center, right, full
      borderRadius: {default: 0, validate: "number"}, // 0-20px border radius
      shadow: {default: "none", validate: "string"}, // none, light, medium, heavy
      ...commonAttrs
    },
    group: "block",
    draggable: true,
    parseDOM: [
      {tag: "figure.image-block", getAttrs(dom: HTMLElement) {
        const img = dom.querySelector('img');
        if (!img) return false;
        
        return {
          src: img.getAttribute("src"),
          title: img.getAttribute("title"),
          alt: img.getAttribute("alt"),
          width: img.getAttribute("width") ? parseInt(img.getAttribute("width")!) : null,
          height: img.getAttribute("height") ? parseInt(img.getAttribute("height")!) : null,
          alignment: img.getAttribute("data-alignment") || "left",
          borderRadius: img.getAttribute("data-border-radius") ? parseInt(img.getAttribute("data-border-radius")!) : 0,
          shadow: img.getAttribute("data-shadow") || "none",
          ...getCommonAttrs(dom)
        };
      }},
      {tag: "img[src]", getAttrs(dom: HTMLElement) {
        return {
          src: dom.getAttribute("src"),
          title: dom.getAttribute("title"),
          alt: dom.getAttribute("alt"),
          width: dom.getAttribute("width") ? parseInt(dom.getAttribute("width")!) : null,
          height: dom.getAttribute("height") ? parseInt(dom.getAttribute("height")!) : null,
          alignment: dom.getAttribute("data-alignment") || "left",
          borderRadius: dom.getAttribute("data-border-radius") ? parseInt(dom.getAttribute("data-border-radius")!) : 0,
          shadow: dom.getAttribute("data-shadow") || "none",
          ...getCommonAttrs(dom)
        }
      }}
    ],
    toDOM(node) { 
      const {src, alt, title, width, height, alignment, borderRadius, shadow} = node.attrs; 
      const attrs: any = {
        src, 
        alt, 
        title,
        "data-alignment": alignment,
        "data-border-radius": borderRadius,
        "data-shadow": shadow,
        ...toCommonDOM(node.attrs)
      };
      if (width) attrs.width = width;
      if (height) attrs.height = height;
      
      // Wrap image in a figure container for better styling control
      return ["figure", {
        class: `image-block image-${alignment}`,
        ...toCommonDOM(node.attrs)
      }, ["img", attrs]];
    }
  } as NodeSpec,

  /// Inline mathematical formula using LaTeX notation
  math_inline: {
    group: "inline",
    inline: true,
    atom: true,
    attrs: {
      latex: {default: "", validate: "string"},
      ...commonAttrs
    },
    parseDOM: [{
      tag: "span.math-inline",
      getAttrs: (dom: HTMLElement) => ({
        latex: dom.getAttribute("data-latex") || "",
        ...getCommonAttrs(dom)
      })
    }],
    toDOM(node) {
      return [
        "span",
        {
          class: "math-inline",
          "data-latex": node.attrs.latex,
          ...toCommonDOM(node.attrs)
        },
        `$${node.attrs.latex}$`
      ];
    }
  } as NodeSpec,

  /// Block-level mathematical formula using LaTeX notation
  math_display: {
    group: "block",
    atom: true,
    attrs: {
      latex: {default: "", validate: "string"},
      ...commonAttrs
    },
    parseDOM: [{
      tag: "div.math-display",
      getAttrs: (dom: HTMLElement) => ({
        latex: dom.getAttribute("data-latex") || "",
        ...getCommonAttrs(dom)
      })
    }],
    toDOM(node) {
      return [
        "div",
        {
          class: "math-display",
          "data-latex": node.attrs.latex,
          ...toCommonDOM(node.attrs)
        },
        `$$${node.attrs.latex}$$`
      ];
    }
  } as NodeSpec,

  /// A hard line break, represented in the DOM as `<br>`.
  hard_break: {
    inline: true,
    group: "inline",
    selectable: false,
    parseDOM: [{tag: "br"}],
    toDOM() { return brDOM }
  } as NodeSpec
}

const emDOM: DOMOutputSpec = ["em", 0], strongDOM: DOMOutputSpec = ["strong", 0], codeDOM: DOMOutputSpec = ["code", 0]

/// [Specs](#model.MarkSpec) for the marks in the schema.
export const marks = {
  /// A link. Has `href` and `title` attributes. `title`
  /// defaults to the empty string. Rendered and parsed as an `<a>`
  /// element.
  link: {
    attrs: {
      href: {validate: "string"},
      title: {default: null, validate: "string|null"}
    },
    inclusive: false,
    parseDOM: [{tag: "a[href]", getAttrs(dom: HTMLElement) {
      return {href: dom.getAttribute("href"), title: dom.getAttribute("title")}
    }}],
    toDOM(node) { const {href, title} = node.attrs; return ["a", {href, title}, 0] }
  } as MarkSpec,

  /// An emphasis mark. Rendered as an `<em>` element. Has parse rules
  /// that also match `<i>` and `font-style: italic`.
  em: {
    parseDOM: [
      {tag: "i"}, {tag: "em"},
      {style: "font-style=italic"},
      {style: "font-style=normal", clearMark: m => m.type.name == "em"}
    ],
    toDOM() { return emDOM }
  } as MarkSpec,

  /// A strong mark. Rendered as `<strong>`, parse rules also match
  /// `<b>` and `font-weight: bold`.
  strong: {
    parseDOM: [
      {tag: "strong"},
      // This works around a Google Docs misbehavior where
      // pasted content will be inexplicably wrapped in `<b>`
      // tags with a font-weight normal.
      {tag: "b", getAttrs: (node: HTMLElement) => node.style.fontWeight != "normal" && null},
      {style: "font-weight=400", clearMark: m => m.type.name == "strong"},
      {style: "font-weight", getAttrs: (value: string) => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null},
    ],
    toDOM() { return strongDOM }
  } as MarkSpec,

  // Underline mark — common in Google Docs pastes via text-decoration or <u>
  underline: {
    parseDOM: [
      { tag: "u" },
      { style: "text-decoration", getAttrs: (value: string) => value && /underline/.test(value) ? {} : false },
      { style: "text-decoration-line", getAttrs: (value: string) => value && /underline/.test(value) ? {} : false },
    ],
    toDOM() { return ["span", { style: "text-decoration: underline" }, 0] }
  } as MarkSpec,

  // Strike-through mark — maps from line-through and legacy tags
  strike: {
    parseDOM: [
      { tag: "s" },
      { tag: "strike" },
      { tag: "del" },
      { style: "text-decoration", getAttrs: (value: string) => value && /line-through/.test(value) ? {} : false },
      { style: "text-decoration-line", getAttrs: (value: string) => value && /line-through/.test(value) ? {} : false },
    ],
    toDOM() { return ["span", { style: "text-decoration: line-through" }, 0] }
  } as MarkSpec,

  // Superscript
  superscript: {
    excludes: "subscript",
    parseDOM: [
      { tag: "sup" },
      { style: "vertical-align", getAttrs: (value: string) => value && /super/.test(value) ? {} : false },
    ],
    toDOM() { return ["sup", 0] }
  } as MarkSpec,

  // Subscript
  subscript: {
    excludes: "superscript",
    parseDOM: [
      { tag: "sub" },
      { style: "vertical-align", getAttrs: (value: string) => value && /sub/.test(value) ? {} : false },
    ],
    toDOM() { return ["sub", 0] }
  } as MarkSpec,

  highlight: {
    parseDOM: [
      {tag: "highlight"},
      {tag: "span", getAttrs: (node: HTMLElement) => node.classList.contains("highlight") ? {} : false},
    ],
    toDOM() { return ["span", { class: "highlight" }, 0] }
  } as MarkSpec,


  /// Code font mark. Represented as a `<code>` element.
  code: {
    code: true,
    parseDOM: [{tag: "code"}],
    toDOM() { return codeDOM }
  } as MarkSpec,

  // Text color mark — preserves CSS color
  textColor: {
    attrs: {
      color: { default: null, validate: "string|null" }
    },
    parseDOM: [
      { tag: "span", getAttrs: (dom: HTMLElement) => {
        const color = dom.style && (dom.style as CSSStyleDeclaration).color;
        if (color) return { color };
        return false;
      }},
    ],
    toDOM(mark) {
      const { color } = mark.attrs;
      const attrs: any = {};
      if (color) attrs.style = `color: ${color}`;
      return ["span", attrs, 0];
    }
  } as MarkSpec,

  // Background color mark — preserves CSS background-color
  backgroundColor: {
    attrs: {
      backgroundColor: { default: null, validate: "string|null" }
    },
    parseDOM: [
      { tag: "span", getAttrs: (dom: HTMLElement) => {
        const bg = dom.style && (dom.style as CSSStyleDeclaration).backgroundColor;
        if (bg) return { backgroundColor: bg };
        return false;
      }},
    ],
    toDOM(mark) {
      const { backgroundColor } = mark.attrs;
      const attrs: any = {};
      if (backgroundColor) attrs.style = `background-color: ${backgroundColor}`;
      return ["span", attrs, 0];
    }
  } as MarkSpec,

  /// Font size mark. Rendered as a span with font-size style using points.
  fontSize: {
    group: "textStyle",
    attrs: {
      size: {default: 11, validate: "number"}
    },
    parseDOM: [
      {tag: "span", getAttrs: (dom: HTMLElement) => {
        const fontSize = dom.style.fontSize;
        if (fontSize) {
          // Parse both px and pt values
          if (fontSize.endsWith('pt')) {
            const size = parseFloat(fontSize);
            return isNaN(size) ? false : {size};
          } else if (fontSize.endsWith('px')) {
            // Convert px to pt (px * 0.75 = pt at 96 DPI)
            const pxSize = parseFloat(fontSize);
            const ptSize = Math.round(pxSize * 0.75);
            return isNaN(ptSize) ? false : {size: ptSize};
          } else {
            const size = parseFloat(fontSize);
            return isNaN(size) ? false : {size};
          }
        }
        return false;
      }}
    ],
    toDOM(mark) { 
      return ["span", {style: `font-size: ${mark.attrs.size}pt`}, 0] 
    }
  } as MarkSpec,

  /// Font family mark. Rendered as a span with font-family style.
  fontFamily: {
    group: "textStyle",
    attrs: {
      font: {default: "", validate: "string"}
    },
    parseDOM: [
      {tag: "span", getAttrs: (dom: HTMLElement) => {
        const fontFamily = dom.style.fontFamily;
        if (fontFamily) {
          return {font: fontFamily};
        }
        return false;
      }}
    ],
    toDOM(mark) { 
      return ["span", {style: `font-family: ${mark.attrs.font}`}, 0] 
    }
  } as MarkSpec,

  /// Font weight mark. Rendered as a span with font-weight style.
  fontWeight: {
    group: "textStyle",
    attrs: {
      weight: {default: "400", validate: "string"}
    },
    parseDOM: [
      {tag: "span", getAttrs: (dom: HTMLElement) => {
        const fontWeight = dom.style.fontWeight;
        if (fontWeight && fontWeight !== "400" && fontWeight !== "normal") {
          return {weight: fontWeight};
        }
        return false;
      }}
    ],
    toDOM(mark) { 
      return ["span", {style: `font-weight: ${mark.attrs.weight}`}, 0] 
    }
  } as MarkSpec,

  /// A deletion mark for tracking changes. Rendered with red background.
  deletion: {
    attrs: {
      revision_id: {default: null},
      batch_description: {default: null},
      marked_at: {default: null}
    },
    inclusive: false,
    parseDOM: [{tag: "span[data-deletion]"}],
    toDOM(mark) { 
      return ["span", {
        "data-deletion": "true",
        "data-revision-id": mark.attrs.revision_id,
        "data-batch-description": mark.attrs.batch_description,
        "data-marked-at": mark.attrs.marked_at,
        style: "background-color: rgba(239, 68, 68, 0.2);"
      }, 0] 
    }
  } as MarkSpec,

  /// An insertion mark for tracking changes. Rendered with green background.
  insertion: {
    attrs: {
      revision_id: {default: null},
      batch_description: {default: null},
      marked_at: {default: null}
    },
    inclusive: false,
    parseDOM: [{tag: "span[data-insertion]"}],
    toDOM(mark) { 
      return ["span", {
        "data-insertion": "true",
        "data-revision-id": mark.attrs.revision_id,
        "data-batch-description": mark.attrs.batch_description,
        "data-marked-at": mark.attrs.marked_at,
        style: "background-color: rgba(34, 197, 94, 0.2);"
      }, 0] 
    }
  } as MarkSpec,

  /// Comment thread anchor mark
  comment: {
    attrs: {
      threadId: { default: null },
      resolved: { default: false }
    },
    inclusive: false,
    excludes: '', // Allow with other marks
    parseDOM: [{
      tag: 'span[data-thread-id]',
      getAttrs: (dom: HTMLElement) => ({
        threadId: dom.getAttribute('data-thread-id'),
        resolved: dom.getAttribute('data-resolved') === 'true'
      })
    }],
    toDOM(mark) {
      return [
        'span',
        {
          'data-thread-id': mark.attrs.threadId,
          'data-resolved': mark.attrs.resolved,
          'class': `comment-anchor ${mark.attrs.resolved ? 'resolved' : 'active'}`
        },
        0
      ];
    }
  } as MarkSpec,

  // Named character style mark
  character_style: {
    excludes: "fontFamily fontSize fontWeight textColor backgroundColor",
    attrs: {
      styleId: { default: null, validate: 'string|null' },
    },
    inclusive: false,
    parseDOM: [{ tag: 'span[data-character-style-id]', getAttrs(dom: HTMLElement) {
      return { styleId: dom.getAttribute('data-character-style-id') };
    }}],
    toDOM(mark) {
      const id = mark.attrs.styleId;
      const cls = id ? `pm-charstyle-${id}` : undefined;
      const attrs: any = { 'data-character-style-id': id };
      if (cls) attrs.class = cls;
      return ['span', attrs, 0];
    }
  } as MarkSpec,

  // AI content detection mark - highlights potentially AI-generated text
  ai_detected: {
    attrs: {
      confidence: { default: "medium" },
      reason: { default: null },
      run_id: { default: null }
    },
    inclusive: false,
    excludes: '',
    parseDOM: [{
      tag: 'span[data-ai-detected]',
      getAttrs: (dom: HTMLElement) => ({
        confidence: dom.getAttribute('data-confidence') || 'medium',
        reason: dom.getAttribute('data-reason'),
        run_id: dom.getAttribute('data-run-id')
      })
    }],
    toDOM(mark) {
      return [
        'span',
        {
          'data-ai-detected': 'true',
          'data-confidence': mark.attrs.confidence,
          'data-reason': mark.attrs.reason || '',
          'data-run-id': mark.attrs.run_id || '',
          'class': `ai-detected-span confidence-${mark.attrs.confidence}`
        },
        0
      ];
    }
  } as MarkSpec,
}
// Helper function to ensure attributes are never null/undefined for Y.js compatibility
const sanitizeMarks = (marks: any) => {
  const sanitizedMarks = { ...marks };
  
  // Ensure suggestion marks have safe default values
  if (sanitizedMarks.suggestion_insert) {
    sanitizedMarks.suggestion_insert = {
      ...sanitizedMarks.suggestion_insert,
      attrs: {
        username: { default: 'Anonymous' },
        data: { default: {} }, // Change from null to empty object
      }
    };
  }
  
  if (sanitizedMarks.suggestion_delete) {
    sanitizedMarks.suggestion_delete = {
      ...sanitizedMarks.suggestion_delete,
      attrs: {
        username: { default: 'Anonymous' },
        data: { default: {} }, // Change from null to empty object
      }
    };
  }
  
  return sanitizedMarks;
};

// Helper functions for enhanced table parsing
function getTableAlignment(style: CSSStyleDeclaration): string {
  const marginLeft = style.marginLeft;
  const marginRight = style.marginRight;
  
  if (marginLeft === "auto" && marginRight === "auto") return "center";
  if (marginLeft === "auto") return "right";
  return "left";
}

function getBorderStyle(borderStyle: string): string {
  if (!borderStyle || borderStyle === "none") return "none";
  return borderStyle === "solid" ? "single" : borderStyle;
}

function getBorderWidth(borderWidth: string): number {
  if (!borderWidth) return 1;
  const match = borderWidth.match(/(\d+)/);
  return match ? parseInt(match[1]) : 1;
}

function parseBorder(border: string): string | null {
  if (!border || border === "none") return null;
  return border;
}

export const schema = new Schema({nodes,
  marks: sanitizeMarks(addSuggestionMarks(marks))})